/**
 * useA2UIActions — Registers CopilotKit actions for A2UI inline rendering.
 *
 * Each `useCopilotAction` with a `render` prop tells CopilotKit to display
 * a React component inline in the chat when the backend emits a matching
 * ACTION_EXECUTION_START event. The component receives args from the
 * ACTION_EXECUTION_ARGS event.
 *
 * This hook must be called inside a CopilotKit context (i.e., inside the
 * RightSidebarCopilotInner component).
 */
import React, { useContext, useEffect, useRef } from 'react';
import { useCopilotAction } from '@copilotkit/react-core';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { LocaleContext, type SupportedLocale } from '@/contexts/LocaleContext';

type SetLocaleFn = (locale: SupportedLocale) => void;
import {
  StatusCard,
  ConfirmationDialog,
  ProgressSteps,
  ChannelStatusCard,
  SetupActionButton,
  InlineForm,
} from './ActionComponents';
import { MutationResultCard } from './MutationResultCard';

interface UseA2UIActionsOptions {
  /** Current interaction mode: 'a2ui' renders inline, 'navigation' uses left panel */
  mode: 'a2ui' | 'navigation';
}

/**
 * Tiny component rendered inline by CopilotKit that performs client-side
 * navigation via useEffect.  Server-driven TOOL_CALL events bypass the
 * `handler` callback — side-effects must live inside the render tree.
 */
function NavigateEffect({ path, navigate }: { path: string; navigate: NavigateFunction }) {
  const done = useRef(false);
  const previousPath = useRef(window.location.pathname + window.location.search);
  useEffect(() => {
    if (path && !done.current) {
      previousPath.current = window.location.pathname + window.location.search;
      done.current = true;
      navigate(path);
    }
  }, [path, navigate]);

  const label = path.split('/').filter(Boolean).pop() || 'page';
  const section = label.split('?')[0];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <StatusCard
        title="Navigation"
        items={[{
          label: section.charAt(0).toUpperCase() + section.slice(1),
          value: done.current ? 'Opened' : 'Opening...',
          status: done.current ? 'ok' : 'pending',
        }]}
      />
      {done.current && previousPath.current !== path && (
        <button
          onClick={() => navigate(previousPath.current)}
          style={{
            background: 'none',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '6px',
            padding: '4px 10px',
            fontSize: '12px',
            cursor: 'pointer',
            color: 'var(--text-secondary, #64748b)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: 'fit-content',
          }}
        >
          ← Back
        </button>
      )}
    </div>
  );
}

const LOCALE_LABELS: Record<string, string> = { fr: 'Français', en: 'English', es: 'Español' };

function ChangeLocaleEffect({ locale, setLocale }: { locale: string; setLocale: SetLocaleFn }) {
  const done = useRef(false);
  useEffect(() => {
    if (locale && !done.current) {
      done.current = true;
      setLocale(locale as SupportedLocale);
    }
  }, [locale, setLocale]);

  return (
    <StatusCard
      title="Language"
      items={[{
        label: 'Platform language',
        value: done.current ? LOCALE_LABELS[locale] || locale : 'Changing...',
        status: done.current ? 'ok' : 'pending',
      }]}
    />
  );
}

export function useA2UIActions({ mode }: UseA2UIActionsOptions) {
  const navigate = useNavigate();
  const { setLocale } = useContext(LocaleContext);

  // ── Core: Navigation ──────────────────────────────────────────────────
  // NOTE: Server-driven TOOL_CALL events bypass `handler` — navigation must
  // happen inside the render function. NavigateEffect uses useEffect to
  // trigger navigate() when the path arrives from the backend.
  useCopilotAction({
    name: 'navigate_console',
    description: 'Navigate the main console area to a specific page',
    parameters: [
      { name: 'path', type: 'string', description: 'Console route path', required: true },
      { name: 'tab', type: 'string', description: 'Optional sub-tab to select' },
    ],
    render: ({ args }: { args: any }) => {
      const path = args.path || '';
      return <NavigateEffect path={path} navigate={navigate} />;
    },
    handler: async ({ path }: { path: string; tab?: string }) => {
      navigate(path);
    },
  });

  // ── Core: Change Language ────────────────────────────────────────────
  useCopilotAction({
    name: 'change_language',
    description: 'Change the platform UI language',
    parameters: [
      { name: 'locale', type: 'string', description: 'Language code: fr, en, or es', required: true },
    ],
    render: ({ args }: { args: any }) => {
      const locale = args.locale || 'en';
      return <ChangeLocaleEffect locale={locale} setLocale={setLocale} />;
    },
    handler: async ({ locale }: { locale: string }) => {
      // Handler fires in main React context — set locale here as primary mechanism
      setLocale(locale as SupportedLocale);
      return { changed: true, locale };
    },
  });

  // ── Core: Status Card ─────────────────────────────────────────────────
  useCopilotAction({
    name: 'show_status_card',
    description: 'Render a key-value status summary card inline in chat',
    parameters: [
      { name: 'title', type: 'string', required: true },
      { name: 'items', type: 'object[]', description: 'Array of {label, value, status}' },
    ],
    render: ({ args }: { args: any }) => {
      const items = Array.isArray(args.items) ? args.items : [];
      return <StatusCard title={args.title || 'Status'} items={items} />;
    },
    handler: async (args: any) => ({ displayed: true }),
  });

  // ── Core: Confirmation Dialog ─────────────────────────────────────────
  useCopilotAction({
    name: 'show_confirmation',
    description: 'Render a yes/no confirmation dialog inline in chat',
    parameters: [
      { name: 'title', type: 'string', required: true },
      { name: 'description', type: 'string', required: true },
    ],
    render: ({ args, handler }: { args: any; handler: any }) => {
      const [loading, setLoading] = React.useState(false);
      const [resolved, setResolved] = React.useState<boolean | null>(null);
      return resolved !== null ? (
        <StatusCard
          title={args.title}
          items={[{ label: 'Decision', value: resolved ? 'Confirmed' : 'Cancelled', status: resolved ? 'ok' : 'error' }]}
        />
      ) : (
        <ConfirmationDialog
          title={args.title || 'Confirm'}
          description={args.description || ''}
          isLoading={loading}
          onConfirm={() => { setLoading(true); setResolved(true); handler?.({ confirmed: true }); }}
          onCancel={() => { setResolved(false); handler?.({ confirmed: false }); }}
        />
      );
    },
    handler: async (args: any) => ({ confirmed: true }),
  });

  // ── Core: Progress Steps ──────────────────────────────────────────────
  useCopilotAction({
    name: 'show_progress',
    description: 'Render a multi-step progress indicator inline in chat',
    parameters: [
      { name: 'title', type: 'string' },
      { name: 'steps', type: 'object[]', description: 'Array of {label, status}' },
    ],
    render: ({ args }: { args: any }) => {
      const steps = Array.isArray(args.steps) ? args.steps : [];
      return <ProgressSteps title={args.title} steps={steps} />;
    },
    handler: async (args: any) => ({ displayed: true }),
  });

  // ── Channels: Status Card ─────────────────────────────────────────────
  useCopilotAction({
    name: 'show_channel_status',
    description: 'Show all channel statuses for an agent',
    parameters: [
      { name: 'channels', type: 'object[]', description: 'Array of channel info' },
    ],
    render: ({ args }: { args: any }) => {
      const channels = Array.isArray(args.channels) ? args.channels : [];
      return <ChannelStatusCard channels={channels} />;
    },
    handler: async (args: any) => ({ displayed: true }),
  });

  // ── Channels: Setup Voice ─────────────────────────────────────────────
  useCopilotAction({
    name: 'setup_voice',
    description: 'Enable voice channel for an agent',
    parameters: [
      { name: 'agent_slug', type: 'string', required: true },
      { name: 'message', type: 'string' },
    ],
    render: ({ args, status }: { args: any; status: string }) => (
      <SetupActionButton
        label="Enable Voice"
        description={args.message || 'Set up voice calls for this agent'}
        icon="📞"
        isComplete={status === 'complete'}
        isLoading={status === 'executing'}
        onClick={() => {}}
      />
    ),
    handler: async (args: any) => ({ success: true }),
  });

  // ── Channels: Setup SMS ───────────────────────────────────────────────
  useCopilotAction({
    name: 'setup_sms',
    description: 'Enable SMS channel for an agent',
    parameters: [
      { name: 'agent_slug', type: 'string', required: true },
      { name: 'phone', type: 'string' },
    ],
    render: ({ args, status }: { args: any; status: string }) => (
      <SetupActionButton
        label="Enable SMS"
        description={args.phone ? `Using ${args.phone}` : 'Set up SMS for this agent'}
        icon="💬"
        isComplete={status === 'complete'}
        isLoading={status === 'executing'}
        onClick={() => {}}
      />
    ),
    handler: async (args: any) => ({ success: true }),
  });

  // ── Channels: Setup Webchat ───────────────────────────────────────────
  useCopilotAction({
    name: 'setup_webchat',
    description: 'Enable/disable webchat channel',
    parameters: [
      { name: 'agent_slug', type: 'string', required: true },
      { name: 'enabled', type: 'boolean' },
    ],
    render: ({ args, status }: { args: any; status: string }) => (
      <SetupActionButton
        label={args.enabled === false ? 'Disable Webchat' : 'Enable Webchat'}
        description="Toggle the embedded web chat widget"
        icon="🌐"
        isComplete={status === 'complete'}
        isLoading={status === 'executing'}
        onClick={() => {}}
      />
    ),
    handler: async (args: any) => ({ success: true }),
  });

  // ── Channels: Setup WhatsApp ──────────────────────────────────────────
  useCopilotAction({
    name: 'setup_whatsapp',
    description: 'Initiate WhatsApp setup (OAuth flow)',
    parameters: [
      { name: 'agent_slug', type: 'string', required: true },
      { name: 'message', type: 'string' },
      { name: 'instructions', type: 'string' },
    ],
    render: ({ args, status }: { args: any; status: string }) => {
      if (mode === 'navigation') {
        // In navigation mode, open the channels page instead
        return (
          <SetupActionButton
            label="Open WhatsApp Setup"
            description="Opens channel configuration page"
            icon="📱"
            isComplete={false}
            onClick={() => navigate(`/agents/${args.agent_slug}/channels`)}
          />
        );
      }
      return (
        <SetupActionButton
          label="Connect WhatsApp"
          description={args.message || 'Complete Meta Business Login to connect WhatsApp'}
          icon="📱"
          isComplete={status === 'complete'}
          isLoading={status === 'executing'}
          onClick={() => {
            // Open WhatsApp channels page for OAuth flow
            navigate(`/agents/${args.agent_slug}/channels`);
          }}
        />
      );
    },
    handler: async (args: any) => ({ success: true, requires_oauth: true }),
  });

  // ── Config: Inline Offering Form ──────────────────────────────────────
  useCopilotAction({
    name: 'inline_offering_form',
    description: 'Quick-add offering without leaving chat',
    parameters: [
      { name: 'agent_slug', type: 'string', required: true },
    ],
    render: ({ args }: { args: any }) => {
      if (mode === 'navigation') {
        return (
          <SetupActionButton
            label="Open Offerings"
            description="Navigate to offerings configuration"
            icon="📦"
            isComplete={false}
            onClick={() => navigate(`/agents/${args.agent_slug}/configuration`)}
          />
        );
      }
      return (
        <InlineForm
          title="Add Offering"
          fields={[
            { name: 'name', label: 'Offering name', type: 'text', required: true },
            { name: 'description', label: 'Description', type: 'text' },
            { name: 'price', label: 'Price', type: 'number' },
          ]}
          onSubmit={(data) => {
            // The form submission result goes back to CopilotKit
            console.log('Offering form submitted:', data);
          }}
        />
      );
    },
    handler: async (args: any) => ({ displayed: true }),
  });

  // ── Config: Rule Preview ──────────────────────────────────────────────
  useCopilotAction({
    name: 'inline_rule_preview',
    description: 'Show rule summary with status',
    parameters: [
      { name: 'rules', type: 'object[]', description: 'Array of rule summaries' },
    ],
    render: ({ args }: { args: any }) => {
      const rules = Array.isArray(args.rules) ? args.rules : [];
      return (
        <StatusCard
          title="Rules"
          items={rules.map((r: any) => ({
            label: r.name || r.label || 'Rule',
            value: r.status || 'active',
            status: r.status === 'active' ? 'ok' : 'pending',
          }))}
        />
      );
    },
    handler: async (args: any) => ({ displayed: true }),
  });

  // ── Config: Agent Card ────────────────────────────────────────────────
  useCopilotAction({
    name: 'inline_agent_card',
    description: 'Show agent summary with health',
    parameters: [
      { name: 'name', type: 'string' },
      { name: 'slug', type: 'string' },
      { name: 'channels', type: 'object[]' },
      { name: 'rules_count', type: 'number' },
    ],
    render: ({ args }: { args: any }) => (
      <StatusCard
        title={args.name || 'Agent'}
        items={[
          { label: 'Slug', value: args.slug || '—', status: 'ok' },
          { label: 'Channels', value: String(args.channels?.length ?? 0), status: (args.channels?.length ?? 0) > 0 ? 'ok' : 'warning' },
          { label: 'Rules', value: String(args.rules_count ?? 0), status: (args.rules_count ?? 0) > 0 ? 'ok' : 'warning' },
        ]}
      />
    ),
    handler: async (args: any) => ({ displayed: true }),
  });

  // ── Mutation Result Card — inline A2UI after tool mutations ─────────
  useCopilotAction({
    name: 'show_mutation_result',
    description: 'Show structured result card after a tool mutation (create/update/delete)',
    parameters: [
      { name: 'entity_type', type: 'string' },
      { name: 'mutation_action', type: 'string' },
      { name: 'entity_data', type: 'object' },
      { name: 'empty_fields', type: 'object' },
      { name: 'suggestions', type: 'object' },
      { name: 'next_actions', type: 'object' },
    ],
    render: ({ args }: { args: any }) => (
      <MutationResultCard
        type={args.entity_type || 'unknown'}
        action={args.mutation_action || 'create'}
        data={args.entity_data || {}}
        emptyFields={Array.isArray(args.empty_fields) ? args.empty_fields : []}
        suggestions={args.suggestions || {}}
        nextActions={Array.isArray(args.next_actions) ? args.next_actions : []}
      />
    ),
    handler: async (args: any) => ({ displayed: true }),
  });
}
